import { Box, Flex, useMantineTheme } from '@mantine/core';
import type {
	AutomationTimeSeries,
	AutomationTimeSeriesPoint,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { BaseOverviewChart } from '../../../../components/Overview/BaseOverviewChart';
import type { StackedLineChartChannel } from '../../../../utils/charts/StackedLineChart';
import { dateFilterOptions } from '../../constants';
import type { TimeRangeValue } from '../../types';
import { processTimeseriesData } from './utils';

export const AutomationsOverviewChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
	}: {
		timeseries: AutomationTimeSeries;
		dateFilter: string;
		setDateFilter: (dateFilter: string) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: string;
		setDateRangeOption: (dateRangeOption: string) => void;
	}) => {
		const theme = useMantineTheme();

		const chartData = useMemo(
			() => processTimeseriesData(timeseries?.points),
			[timeseries]
		);

		const channels = useMemo(
			() =>
				[
					{
						label: 'Automation runs',
						x: (d: AutomationTimeSeriesPoint) => new Date(d.timestamp),
						y: (d: AutomationTimeSeriesPoint) => d.total_automations as number,
						renderArea: true,
						areaOpacity: 0.3,
						color: theme.other.getColor('icon/primary/default'),
						zIndex: 10,
					},
					{
						label: 'Tasks automated',
						x: (d: AutomationTimeSeriesPoint) => new Date(d.timestamp),
						y: (d: AutomationTimeSeriesPoint) =>
							d.total_tasks_automated as number,
						renderArea: true,
						areaOpacity: 0.3,
						color: theme.other.getColor('icon/emphasis/default'),
						zIndex: 20,
					},
				] as StackedLineChartChannel<Record<string, unknown>, Date, number>[],
			[theme]
		);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.timestamp.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : (
								point.timestamp.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[0].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Automation runs </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.total_automations}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[1].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Tasks automated </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.total_tasks_automated}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[chartData, dateRangeOption, channels, theme]
		);

		// Create a modified timeseries object that includes the required available_dimensions
		const modifiedTimeseries = useMemo(
			() => ({
				...timeseries,
				available_dimensions: [] as string[],
			}),
			[timeseries]
		);

		return (
			<BaseOverviewChart
				timeseries={modifiedTimeseries}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={dateRangeOption}
				setDateRangeOption={setDateRangeOption}
				title="Tasks automated over time"
				dateFilterOptions={dateFilterOptions}
				channels={channels}
				tooltipDataRenderer={tooltipDataRenderer}
				analyticsEventPrefix="automations/overview"
			/>
		);
	}
);
